"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useNextSanityImage } from "next-sanity-image";
import { cn } from "@repo/ui/utils";
import { client } from "@/lib/sanity/client";
import { getObjectPositionFromHotspot } from "@/lib/utils";
import type { LogoImage } from "./logo-tile.types";
interface LogoTileProps {
  /** Logo image projected from a `logo` document's `logoLight` field. */
  logo: LogoImage | null;
  /** Fallback alt text used when the asset has no `altText` (typically the entity name). */
  alt: string;
  /** Additional CSS class names */
  className?: string;
}

/**
 * Shared logo tile used by `ResourceCard` and `CommercialTenantCard`.
 * Renders a light-coloured rounded surface with the supplied logo centred,
 * padded, and `object-contain`'d so the logo's native aspect ratio is
 * preserved. The outer surface renders even when the logo is missing so
 * grid layouts stay stable.
 */
export function LogoTile(t0) {
  const $ = _c(9);
  const {
    logo,
    alt,
    className
  } = t0;
  const imageProps = useNextSanityImage(client, logo);
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative aspect-171/128 w-full overflow-hidden rounded-md desktop:aspect-[310/175]", "bg-surface-base-secondary", "transition-shadow duration-300 ease-out", "group-hover:shadow-md", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== alt || $[3] !== imageProps || $[4] !== logo) {
    t2 = logo?.asset && imageProps && <div className="flex size-full items-center justify-center p-4 desktop:p-8"><div className="relative h-full w-full"><Image alt={logo.asset.altText || alt} blurDataURL={logo.asset.metadata?.lqip || ""} className="h-full w-full object-contain" fill={true} loader={imageProps.loader} placeholder={logo.asset.metadata?.lqip ? "blur" : "empty"} quality={85} sizes="(max-width: 1024px) 50vw, 25vw" src={imageProps.src} style={{
          objectPosition: getObjectPositionFromHotspot(logo.hotspot ?? {
            x: 0.5,
            y: 0.5
          })
        }} /></div></div>;
    $[2] = alt;
    $[3] = imageProps;
    $[4] = logo;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className={t1} data-testid="logo-tile">{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}